import { useTranslation } from "react-i18next";

import { cn } from "@erp/ui";

/**
 * Mizan (ميزان — "the balance") brand mark: a geometric scale whose two pans
 * are equal squares — one filled, one outlined. Debit and credit: same size,
 * opposite fill, always level. Renders in currentColor with the accent taken
 * from the primary token, so it works on any surface and at favicon size.
 */
export function LogoMark({ className, accent = true }: { className?: string; accent?: boolean }) {
  return (
    <svg
      viewBox="0 0 32 32"
      role="img"
      aria-hidden="true"
      className={cn("size-8", className)}
      fill="none"
    >
      {/* beam */}
      <rect x="5" y="8" width="22" height="2.6" rx="1.3" fill="currentColor" />
      {/* post */}
      <rect x="14.7" y="8" width="2.6" height="15" rx="1.3" fill="currentColor" />
      {/* base */}
      <rect x="10" y="23.4" width="12" height="2.6" rx="1.3" fill="currentColor" />
      {/* debit pan — filled */}
      <rect
        x="4"
        y="13.4"
        width="7"
        height="7"
        rx="1.6"
        fill={accent ? "var(--color-primary)" : "currentColor"}
      />
      {/* credit pan — outlined, same size */}
      <rect
        x="21"
        y="13.4"
        width="7"
        height="7"
        rx="1.6"
        stroke={accent ? "var(--color-primary)" : "currentColor"}
        strokeWidth="2"
      />
    </svg>
  );
}

/** Mark + bilingual wordmark. `panel` inverts for the dark auth/brand panel. */
export function LogoWordmark({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn("flex items-center gap-2.5", className)}>
      <LogoMark className="size-8 shrink-0" />
      <span className="flex flex-col leading-none">
        <span className="text-lg font-extrabold tracking-wide">{t("app.name")}</span>
        <span className="text-[0.6875rem] font-medium tracking-[0.22em] uppercase opacity-60">
          {t("app.kind", "ERP")}
        </span>
      </span>
    </span>
  );
}
